import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { projectsApi } from '@api/projects';
import { refreshSessionFromCookie } from '@api/http';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { ProjectFormModal } from '../components/ProjectFormModal';
import type { Project, ProjectInput } from '../types';

const { Text } = Typography;

export function ProjectsPage() {
  const { t } = useTranslation(['projects', 'common']);
  const qc = useQueryClient();
  const confirm = useConfirm();
  const canManage = useCan(PERMISSIONS.PROJECT_MANAGE);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['projects'] });

  // REQ-45 — proje CRUD (oluştur/güncelle/sil, isDefault dahil) BE'deki
  // erişilebilir-proje kümesini değiştirir. Oturumu tazeleyerek store'daki
  // `availableProjects`'i güncelle → Topbar switcher logout'suz yeni durumu
  // yansıtır (yalnız login/refresh yanıtıyla beslenmesin).
  const refreshSession = () => refreshSessionFromCookie();

  const createMutation = useMutation({
    mutationFn: (input: ProjectInput) => projectsApi.create(input),
    onSuccess: async () => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
      await refreshSession();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: ProjectInput }) =>
      projectsApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('toast.updated'));
      setModalOpen(false);
      await invalidate();
      await refreshSession();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectsApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
      await refreshSession();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (project: Project) => {
    setEditing(project);
    setModalOpen(true);
  };
  const requestDelete = (project: Project) => {
    void (async () => {
      const ok = await confirm({
        title: t('delete.title'),
        description: t('delete.confirm', { name: project.name }),
        okText: t('delete.ok'),
      });
      if (ok) deleteMutation.mutate(project.id);
    })();
  };

  const columns: ColumnsType<Project> = [
    {
      title: t('columns.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: t('columns.slug'),
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <Text code>{slug}</Text>,
    },
    {
      title: t('columns.domain'),
      dataIndex: 'domain',
      key: 'domain',
      render: (domain: string | null) => domain || '—',
    },
    {
      title: t('columns.default'),
      key: 'default',
      render: (_, r) =>
        r.isDefault ? (
          <Tag color="gold" icon={<Star size={12} />}>
            {t('badges.default')}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: t('columns.active'),
      key: 'active',
      render: (_, r) =>
        r.isActive ? (
          <Tag color="green">{t('common:states.active')}</Tag>
        ) : (
          <Tag>{t('common:states.inactive')}</Tag>
        ),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            aria-label={t('form.editTitle')}
          />
          <Tooltip title={r.isDefault ? t('delete.defaultBlockedTooltip') : undefined}>
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              onClick={() => requestDelete(r)}
              // Varsayılan proje silinemez (BE invariant) — buton kapalı + tooltip.
              disabled={!canManage || r.isDefault}
              loading={deleteMutation.isPending && deleteMutation.variables === r.id}
              aria-label={t('delete.title')}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="projects-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <Table<Project>
          rowKey="id"
          columns={columns}
          dataSource={projects}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: t('empty') }}
        />
      </Card>

      <ProjectFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />
    </div>
  );
}
